import { Button } from '@onestep/coreui/Button';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  attemptStaleChunkRecovery,
  isReloadPending,
  isStaleChunkError,
} from '@/services/staleChunkRecovery';

import errorIllustration from '../assets/images/GlobalErrorImages/error-illustration.svg?url';
import useMixpanel from '../hooks/useMixpanel';
import { DEFAULT_NS } from '../i18n/types';
import type { GlobalErrorFallbackProps } from './types';

export const GlobalErrorFallback = ({ error }: GlobalErrorFallbackProps) => {
  const { t: tCommon } = useTranslation(DEFAULT_NS, { keyPrefix: 'common' });
  const [showError, setShowError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [recoveryDeclined, setRecoveryDeclined] = useState(false);

  const { trackFail } = useMixpanel();

  const refresh = () => {
    setIsLoading(true);
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  useEffect(() => {
    if (isReloadPending()) return;
    if (error) {
      if (attemptStaleChunkRecovery(error)) {
        return;
      }
      trackFail('white_screen', {
        error_name: error.name,
        error_message: error.message,
        error_stack: error.stack,
      });
      setRecoveryDeclined(true);
    }
  }, [error]);

  if (isReloadPending() || (isStaleChunkError(error) && !recoveryDeclined)) return null;

  return (
    <div
      role={'heading'}
      data-name={'crash app page'}
      data-testid={'error_white_screen'}
      className="flex min-h-screen w-full flex-row items-stretch"
    >
      <div className="flex w-full flex-col items-start justify-center gap-6 bg-primary-m3 px-16 lg:w-1/2">
        <div className="flex flex-col gap-2">
          <h1 className="text-4xl font-extrabold leading-normal text-neutral-p3">
            <span className="block">Oops,</span>
            <span className="block">something went wrong.</span>
          </h1>
          <span className="text-2.5xl text-neutral-p2">
            Refresh the page to try again. Contact support if the issue continues.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="medium"
            onClick={refresh}
            disabled={isLoading}
            label={isLoading ? 'Refreshing...' : 'Refresh page'}
          />
          <Button
            variant="ghost"
            size="medium"
            onClick={() => setShowError(true)}
            label={tCommon('learnMore')}
          />
        </div>
        {showError && error ? (
          <p className="text-sm text-error-p2">{error.message || error.stack}</p>
        ) : null}
      </div>
      <div className="hidden w-1/2 items-center justify-center bg-neutral-m5 lg:flex">
        <img alt="" className="max-w-md" src={errorIllustration} />
      </div>
    </div>
  );
};
